// context/PermissionsContext.tsx
'use client'
import React, { createContext, useContext, ReactNode, useEffect, useState, useRef } from 'react'
import { getSupabaseBrowserClient } from '@/lib/supabase'
import { hasPermission, Permission, UserRole, hasAccessToBranch, hasAccessToMemorial, getPermissionsForRole } from '@/lib/permissions'
import { getImpersonatedUserId } from '@/lib/impersonation'

type ExtendedUserData = {
  id?: string
  role?: string
  custom_role_id?: string | null
  bestatterId?: string
  bestatter_id?: string
  branchId?: string
  branch_id?: string | null
  is_branch?: boolean
  parentBestatterId?: string
  parent_bestatter_id?: string
  sds_customer_id?: string | null
  company_name?: string
  company_logo?: string
  branding_enabled?: boolean
  permissions?: string[] | null
  [key: string]: any
}

type CustomRole = {
  id: string
  name: string
  key: string
  permissions: string[]
  description?: string
  is_system: boolean
}

type PermissionsContextType = {
  userId: string | null
  userRole: string | null
  customRole: CustomRole | null
  userPermissions: Permission[]
  bestatterId: string | null
  branchId: string | null
  isBranch: boolean
  parentBestatterId: string | null
  sdsCustomerId: string | null
  sds_customer_id: string | null
  companyName: string | null
  companyLogo: string | null
  brandingEnabled: boolean
  loading: boolean
  isLoading?: boolean
  can: (permission: Permission) => boolean
  hasPermission: (permission: Permission) => boolean
  canAccessBranch: (branchId: string) => boolean
  canAccessMemorial: (memorialOwnerId: string) => boolean
  canAccessSDS: () => boolean
  isB2BDisplay: boolean
  isImpersonating: boolean
  clearCache: () => void
  reloadPermissions: () => Promise<void>
}

const PermissionsContext = createContext<PermissionsContextType>({
  userId: null,
  userRole: null,
  customRole: null,
  userPermissions: [],
  bestatterId: null,
  branchId: null,
  isBranch: false,
  parentBestatterId: null,
  sdsCustomerId: null,
  sds_customer_id: null,
  companyName: null,
  companyLogo: null,
  brandingEnabled: true,
  loading: true,
  isLoading: true,
  can: () => false,
  hasPermission: () => false,
  canAccessBranch: () => false,
  canAccessMemorial: () => false,
  canAccessSDS: () => false,
  isB2BDisplay: false,
  isImpersonating: false,
  clearCache: () => {},
  reloadPermissions: async () => {},
})

let systemRoleOverridesCache: Map<string, Permission[]> | null = null
let cacheTimestamp = 0
const CACHE_DURATION = 5 * 60 * 1000

const unionPermissions = (...lists: Array<Permission[] | string[] | null | undefined>): Permission[] => {
  const set = new Set<string>()
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const p of list) {
      if (typeof p === 'string' && p.trim()) set.add(p)
    }
  }
  return Array.from(set) as Permission[]
}

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ExtendedUserData | null>(null)
  const [customRole, setCustomRole] = useState<CustomRole | null>(null)
  const [userPermissions, setUserPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [impersonating, setImpersonating] = useState(false)

  const supabase = getSupabaseBrowserClient()

  const isLoadingRef = useRef(false)
  const lastLoadTimeRef = useRef(0)
  const hasInitialLoadRef = useRef(false)
  const authSubscriptionRef = useRef<any>(null)
  const currentUserIdRef = useRef<string | null>(null)
  const eventDebounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const loadSystemRoleOverrides = async (forceReload = false): Promise<Map<string, Permission[]>> => {
    const now = Date.now()
    if (!forceReload && systemRoleOverridesCache && now - cacheTimestamp < CACHE_DURATION) {
      console.log('📦 Using cached system role overrides')
      return systemRoleOverridesCache
    }

    console.log('🔍 Loading system role overrides from DB...')
    try {
      const { data: roles, error } = await supabase.from('roles').select('key, permissions').eq('is_system', true)

      if (error) {
        console.error('❌ Error loading system role overrides:', error)
        return new Map()
      }

      const overridesMap = new Map<string, Permission[]>()
      if (roles && roles.length > 0) {
        for (const role of roles) {
          if (role.permissions && role.permissions.length > 0) {
            const validPermissions = role.permissions.filter((p: string) => typeof p === 'string') as Permission[]
            overridesMap.set(role.key, validPermissions)
            console.log(`✅ Loaded DB override for ${role.key}: ${validPermissions.length} permissions`)
          } else {
            console.log(`ℹ️ DB override for ${role.key} has empty permissions; will merge with hardcoded (no replace).`)
          }
        }
      }

      systemRoleOverridesCache = overridesMap
      cacheTimestamp = now
      console.log(`📊 Loaded ${overridesMap.size} system role overrides from DB`)
      return overridesMap
    } catch (error) {
      console.error('❌ Failed to load system role overrides:', error)
      return new Map()
    }
  }

  const getPermissionsForRoleWithOverride = async (role: UserRole, overridesMap?: Map<string, Permission[]>): Promise<Permission[]> => {
    const overrides = overridesMap || (await loadSystemRoleOverrides())
    const hardcodedPermissions = getPermissionsForRole(role)
    const dbExtraPermissions = overrides.get(role) || []
    const merged = unionPermissions(hardcodedPermissions, dbExtraPermissions)
    console.log(`🔗 Merged permissions for ${role}: hardcoded=${hardcodedPermissions.length}, dbExtra=${dbExtraPermissions.length}, effective=${merged.length}`)
    return merged
  }

  const loadUserData = async (forceReload = false, authUserId?: string) => {
    if (isLoadingRef.current) {
      console.log('🔄 Load already in progress, skipping...')
      return
    }

    const now = Date.now()
    const timeSinceLastLoad = now - lastLoadTimeRef.current
    if (!forceReload && hasInitialLoadRef.current && timeSinceLastLoad < 2000) {
      console.log(`⏳ Too soon since last load (${timeSinceLastLoad}ms), skipping...`)
      return
    }

    console.log('🔍 Starting to load user data...')
    isLoadingRef.current = true
    lastLoadTimeRef.current = now
    setLoading(true)

    const cleanupToSignedOutState = () => {
      setUser(null)
      setCustomRole(null)
      setUserPermissions([])
      setImpersonating(false)
      currentUserIdRef.current = null
      hasInitialLoadRef.current = true
    }

    try {
      let authUser: any = null

      if (authUserId) {
        authUser = { id: authUserId }
        console.log('🔍 Using provided auth user ID:', authUserId)
      } else {
        const { data: { user: fetchedUser }, error: authError } = await supabase.auth.getUser()
        if (authError) {
          console.error('❌ Auth error:', authError)
          cleanupToSignedOutState()
          return
        }
        authUser = fetchedUser
      }

      if (!authUser) {
        console.log('❌ No authenticated user')
        cleanupToSignedOutState()
        return
      }

      currentUserIdRef.current = authUser.id

      let effectiveUserId = authUser.id
      const impersonatedUserId = getImpersonatedUserId()

      if (impersonatedUserId && impersonatedUserId !== authUser.id) {
        const { data: realUserData } = await supabase.from('users').select('role').eq('id', authUser.id).single()
        if (realUserData?.role === 'admin') {
          effectiveUserId = impersonatedUserId
          setImpersonating(true)
          console.log('🔄 Impersonation aktiv: Admin', authUser.id, '→ User', impersonatedUserId)
        } else {
          setImpersonating(false)
          console.warn('⚠️ Impersonation-Cookie vorhanden aber User ist kein Admin, ignoriere...')
        }
      } else {
        setImpersonating(false)
      }

      const { data: userData, error: userError } = await supabase.from('users').select('*').eq('id', effectiveUserId).single()
      console.log('🔍 User data from DB:', userData ? 'loaded' : 'not found', '(effective ID:', effectiveUserId, ')')

      if (userError || !userData) {
        console.error('❌ Error loading user data:', userError)
        cleanupToSignedOutState()
        return
      }

      setUser(userData)

      const systemOverrides = await loadSystemRoleOverrides(forceReload)

      let effectivePermissions: Permission[] = []
      const userCustomPermissions = (userData.permissions || []) as Permission[]

      if (userData.custom_role_id) {
        console.log('🔍 Loading custom role:', userData.custom_role_id)
        const { data: roleData, error: roleError } = await supabase
          .from('roles')
          .select('id, name, key, permissions, description, is_system')
          .eq('id', userData.custom_role_id)
          .maybeSingle()

        if (!roleError && roleData) {
          setCustomRole(roleData as CustomRole)
          effectivePermissions = unionPermissions((roleData.permissions || []) as Permission[], userCustomPermissions)
          console.log('✅ Custom role loaded:', roleData.name, 'with effective permissions:', effectivePermissions.length)
        } else {
          console.log('⚠️ Custom role not found, falling back to system role')
        }
      }

      if (effectivePermissions.length === 0 && userData.role) {
        const rolePerms = await getPermissionsForRoleWithOverride(userData.role as UserRole, systemOverrides)
        effectivePermissions = unionPermissions(rolePerms, userCustomPermissions)
      }

      setUserPermissions([...effectivePermissions])
      hasInitialLoadRef.current = true

      console.log('🎯 Permissions set in state:', { count: effectivePermissions.length, sample: effectivePermissions.slice(0, 5) })
    } catch (error) {
      console.error('❌ Unexpected error loading user data:', error)
      cleanupToSignedOutState()
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }

  const canAccessSDS = (): boolean => {
    if (user?.role === 'admin') return true
    return userPermissions.some((permission) => permission.startsWith('sds:'))
  }

  const BUSINESS_PRICING_PERMS: Permission[] = [
    Permission.InvoiceGenerateB2B,
    Permission.InvoiceGenerateBestatterCollective,
    Permission.InvoiceGenerateSDSCollective,
    Permission.SDSInvoice,
  ]

  const isB2BDisplay =
    !!user?.is_branch ||
    !!user?.sds_customer_id ||
    (user?.role ? ['bestatter', 'sds_user', 'admin', 'subadmin'].includes(user.role as UserRole) : false) ||
    userPermissions.some((p) => BUSINESS_PRICING_PERMS.includes(p))

  const reloadPermissions = async () => {
    console.log('🔄 Force reloading permissions...')
    systemRoleOverridesCache = null
    cacheTimestamp = 0
    await loadUserData(true)
  }

  useEffect(() => {
    const handlePermissionsUpdate = () => {
      console.log('📢 Permissions update event received')
      reloadPermissions()
    }

    window.addEventListener('permissions-updated', handlePermissionsUpdate)

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'permissions-updated') {
        console.log('📢 Permissions updated in another tab')
        reloadPermissions()
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('permissions-updated', handlePermissionsUpdate)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      loadUserData(true)
    }
  }, [])

  // ✅ FIX: nur einmal subscriben, nicht bei jedem user change neu
  useEffect(() => {
    if (authSubscriptionRef.current) {
      authSubscriptionRef.current.unsubscribe()
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, 'Session user:', session?.user?.id)

      if (eventDebounceTimerRef.current) {
        clearTimeout(eventDebounceTimerRef.current)
        eventDebounceTimerRef.current = null
      }

      switch (event) {
        case 'SIGNED_IN': {
          const newUserId = session?.user?.id
          if (!currentUserIdRef.current || currentUserIdRef.current !== newUserId || !hasInitialLoadRef.current) {
            eventDebounceTimerRef.current = setTimeout(async () => {
              await loadUserData(true, newUserId)
            }, 500)
          }
          break
        }
        case 'SIGNED_OUT': {
          setUser(null)
          setCustomRole(null)
          setUserPermissions([])
          setImpersonating(false)
          currentUserIdRef.current = null
          hasInitialLoadRef.current = false
          isLoadingRef.current = false
          lastLoadTimeRef.current = 0
          systemRoleOverridesCache = null
          cacheTimestamp = 0
          setLoading(false)
          break
        }
        case 'USER_UPDATED': {
          eventDebounceTimerRef.current = setTimeout(async () => {
            await loadUserData(true)
          }, 500)
          break
        }
        case 'INITIAL_SESSION': {
          if (!hasInitialLoadRef.current) {
            const userId = session?.user?.id
            if (userId) {
              eventDebounceTimerRef.current = setTimeout(async () => {
                await loadUserData(true, userId)
              }, 500)
            }
          }
          break
        }
        default:
          break
      }
    })

    authSubscriptionRef.current = subscription

    return () => {
      if (authSubscriptionRef.current) authSubscriptionRef.current.unsubscribe()
      if (eventDebounceTimerRef.current) clearTimeout(eventDebounceTimerRef.current)
    }
  }, []) // ✅ bleibt leer

  const can = (permission: Permission): boolean => {
    if (!impersonating && user?.role === 'admin') return true
    return userPermissions.includes(permission)
  }

  const canAccessBranchCheck = (branchId: string): boolean => {
    if (!user?.id || !user?.role) return false
    if (customRole) return can('branch:view' as Permission) || can('branch:view:all' as Permission)
    const branch = { id: branchId, bestatter_id: branchId }
    return hasAccessToBranch(user.role as UserRole, user.id, user.bestatter_id || null, branch)
  }

  const canAccessMemorialCheck = (memorialOwnerId: string): boolean => {
    if (!user?.id || !user?.role) return false
    if (customRole) return can('memorial:view' as Permission) || can('memorial:view:all' as Permission)
    return hasAccessToMemorial(user.role as UserRole, user.id, user.bestatter_id || null, memorialOwnerId)
  }

  const clearCache = () => {
    console.log('🔄 Clearing permissions cache and reloading...')
    isLoadingRef.current = false
    lastLoadTimeRef.current = 0
    hasInitialLoadRef.current = false
    currentUserIdRef.current = null
    systemRoleOverridesCache = null
    cacheTimestamp = 0
    if (eventDebounceTimerRef.current) {
      clearTimeout(eventDebounceTimerRef.current)
      eventDebounceTimerRef.current = null
    }
    loadUserData(true)
  }

  const sdsCustomerId = user?.sds_customer_id || null

  const contextValue: PermissionsContextType = {
    userId: user?.id || null,
    userRole: user?.role || null,
    customRole,
    userPermissions,
    bestatterId: user?.bestatter_id || null,
    branchId: user?.branch_id || null,
    isBranch: user?.is_branch || false,
    parentBestatterId: user?.parent_bestatter_id || null,
    sdsCustomerId,
    sds_customer_id: sdsCustomerId,
    companyName: user?.company_name || null,
    companyLogo: user?.company_logo || null,
    brandingEnabled: user?.branding_enabled !== false,
    loading,
    isLoading: loading,
    can,
    hasPermission: can,
    canAccessBranch: canAccessBranchCheck,
    canAccessMemorial: canAccessMemorialCheck,
    canAccessSDS,
    isB2BDisplay,
    isImpersonating: impersonating,
    clearCache,
    reloadPermissions,
  }

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>
}

export function usePermissionsContext() {
  const context = useContext(PermissionsContext)
  return context
}
